import React, { useEffect, useRef, useState } from "react";
import "./WorkySalesDemo.css";
import { CircularProgressbar } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import Rating from "@mui/material/Rating";
import { Sparkles, Star, TrendingUp, ChevronDown, Users, Phone, DollarSign, Mail, Link2, Gem, ArrowRight, Globe, Snowflake, Search, Eye, FileText, Calendar, Briefcase, Plus, ChevronRight, Check, Minus } from "lucide-react";
import { useIsMobile } from "../../../custom-hooks/useIsMobile";



// All contacts, organizations, scores and metrics below are illustrative demo data.
interface AnimatedSalesCardProps {
    readonly isAnimationEnabled: boolean;
}

const WorkySalesDemoCardOne = ({ isAnimationEnabled }: AnimatedSalesCardProps): React.JSX.Element => {
    const TARGET_CONFIDENCE: number = 94;
    const [animatedConfidence, setAnimatedConfidence] = useState<number>(0);
    const [signalsVisible, setSignalsVisible] = useState<boolean>(false);

    useEffect((): (() => void) => {
        if (!isAnimationEnabled) {
            setAnimatedConfidence(0);
            return (): void => { return; };
        }

        const animationTimeout: ReturnType<typeof setTimeout> = setTimeout((): void => {
            setAnimatedConfidence(TARGET_CONFIDENCE);
        }, 80);

        return (): void => {
            clearTimeout(animationTimeout);
        };
    }, [TARGET_CONFIDENCE, isAnimationEnabled]);

    useEffect((): (() => void) => {
        if (!isAnimationEnabled) {
            setSignalsVisible(false);
            return (): void => { return; };
        }

        const signalsTimeout: ReturnType<typeof setTimeout> = setTimeout((): void => {
            setSignalsVisible(true);
        }, 180);

        return (): void => {
            clearTimeout(signalsTimeout);
        };
    }, [isAnimationEnabled]);

    return(
        <div className='wsd-card'>
            <div className='wsd-card-one-inner'>
                <div className='wsd-card-one-inner-left'>
                    <span className='wsd-card-one-inner-left-title'>Match Confidence</span>
                    <div className='wsd-card-one-inner-left-visual'>
                        <svg className='wsd-progress-gradient-defs' aria-hidden='true' focusable='false'>
                            <defs>
                                <linearGradient id='worky-sales-progress-gradient' x1='0%' y1='100%' x2='100%' y2='0%'>
                                    <stop offset='0%' stopColor='rgb(245, 158, 11)' />
                                    <stop offset='100%' stopColor='rgb(239, 68, 68)' />
                                </linearGradient>
                            </defs>
                        </svg>
                        <div className='wsd-progress-wrapper'>
                            <CircularProgressbar
                                value={animatedConfidence}
                                strokeWidth={10}
                                styles={{
                                    path: {
                                        stroke: 'url(#worky-sales-progress-gradient)',
                                        transition: 'stroke-dashoffset 1.4s ease-in-out 0s'
                                    },
                                    trail: {
                                        stroke: '#fde6cf'
                                    }
                                }}
                            />
                            <div className='wsd-progress-inner-label'>
                                <span className='wsd-progress-inner-value'>{TARGET_CONFIDENCE}%</span>
                                <span className='wsd-progress-inner-badge'>HOT LEAD</span>
                            </div>
                        </div>
                    </div>
                    <span className='wsd-card-one-inner-left-text'>Based on care needs,<br/>team size and local<br/>staffing requirements.</span>
                </div>
                <div className='wsd-card-one-inner-right'>
                <div className='wsd-card-one-inner-right-inner'>
                    <span className='wsd-card-one-inner-right-title'>Care Provider Lead Intent</span>
                    <span className='wsd-card-one-inner-right-subtitle'>Illustrative healthcare lead</span>

                    <div className="wsd-card-one-inner-right-lead-card">
                        <div className="wsd-card-one-inner-right-lead-card-icon">
                            <img
                                src="https://i.pravatar.cc/60?img=47"
                                alt="Alice Chen"
                                className="wsd-lead-card-avatar"
                            />
                        </div>

                        <div className="wsd-card-one-inner-right-lead-card-text">
                            <span className="wsd-card-one-inner-right-lead-card-text-title">Alice Chen</span>
                            <span className="wsd-card-one-inner-right-lead-card-text-text">Director of Care</span>
                        </div>

                        <div className="wsd-card-one-inner-right-lead-card-text" style={{ marginLeft: 'auto' }}>
                            <span className="wsd-card-one-inner-right-lead-card-text-text">Maple Grove Care</span>
                            <span className="wsd-card-one-inner-right-lead-card-text-text" style={{ marginTop: '0px' }}><span className="wsd-card-one-inner-right-lead-card-text-text-pill">Hiring: PSWs</span><Rating value={4.5} readOnly size="small" sx={{ fontSize: '0.7rem' }} /></span>
                        </div>
                    </div>

                    <div className={`wsd-card-one-inner-right-visual-holder${signalsVisible ? ' wsd-signals--visible' : ''}`}>

                        <div className="wsd-card-one-inner-right-visual wsd-visual--fade-1">
                            <div className="wsd-card-one-inner-right-visual-icon wsd-visual-icon--positive">
                                <svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M2 6l3 3 5-5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </div>
                            <div className="wsd-card-one-inner-right-visual-text">
                                <span className="wsd-card-one-inner-right-visual-text-title">Care staffing interest</span>
                                <span className="wsd-card-one-inner-right-visual-text-text">Viewed PSW and nursing staffing pages in the last 48h.</span>
                            </div>
                        </div>

                        <div className="wsd-card-one-inner-right-visual wsd-visual--fade-2">
                            <div className="wsd-card-one-inner-right-visual-icon wsd-visual-icon--positive">
                                <svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M2 6l3 3 5-5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </div>
                            <div className="wsd-card-one-inner-right-visual-text">
                                <span className="wsd-card-one-inner-right-visual-text-title">Exploring agency support</span>
                                <span className="wsd-card-one-inner-right-visual-text-text">Downloaded the &#8220;Home-care Staffing Guide&#8221;.</span>
                            </div>
                        </div>

                        <div className="wsd-card-one-inner-right-visual wsd-visual--fade-3">
                            <div className="wsd-card-one-inner-right-visual-icon wsd-visual-icon--positive">
                                <svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M2 6l3 3 5-5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </div>
                            <div className="wsd-card-one-inner-right-visual-text">
                                <span className="wsd-card-one-inner-right-visual-text-title">Relevant care team size</span>
                                <span className="wsd-card-one-inner-right-visual-text-text">Care team of 11&#8211;50 staff fits your agency's service capacity.</span>
                            </div>
                        </div>

                        <div className="wsd-card-one-inner-right-visual wsd-visual--no-border wsd-visual--fade-4">
                            <div className="wsd-card-one-inner-right-visual-icon wsd-visual-icon--neutral">
                                <svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M3 6h6" stroke="#b0b0b0" strokeWidth="1.8" strokeLinecap="round"/>
                                </svg>
                            </div>
                            <div className="wsd-card-one-inner-right-visual-text">
                                <span className="wsd-card-one-inner-right-visual-text-title wsd-visual-text-title--neutral">Coverage needs to confirm</span>
                                <span className="wsd-card-one-inner-right-visual-text-text">Weekend and overnight requirements still need discussion.</span>
                            </div>
                        </div>

                    </div>

                    <div className='wsd-card-one-inner-right-footer'>
                        <button style={{padding:'8px 16px', fontSize:'12px', fontWeight:'600'}} className='lp-cta-button lp-navigation-button'>Book Demo</button>
                        <button className='wsd-footer-btn'>Learn More</button>
                    </div>

                    
                </div>
            </div>
            </div>
        </div>
    )
}

const WorkySalesDemoCardTwo = (_props: AnimatedSalesCardProps): React.JSX.Element => {
    return(
        <div className='wsd-card'>
            <div className='wsd-card-two-inner'>

                <div className='wsd-card-two-header'>
                    <div className='wsd-card-two-header-left'>
                        <div className='wsd-card-two-header-title'>
                            Quota Attainment
                            <span className='wsd-card-two-header-pill'>
                                <TrendingUp size={10} strokeWidth={2.5} /> +16% YoY
                            </span>
                        </div>
                        <span className='wsd-card-two-header-subtitle'>Care partnerships overview · Sample Q3</span>
                    </div>
                    <button className='wsd-card-two-header-btn'>
                        Learn More <ChevronDown size={13} strokeWidth={2.5} style={{transform: 'rotate(-90deg)'}}/>
                    </button>
                </div>

                <div className='wsd-card-two-stats-row'>
                    <div className='wsd-card-two-stat-card'>
                        <div className='wsd-card-two-stat-card-top'>
                            <span className='wsd-card-two-stat-label'>Care Provider Meetings</span>
                            <Users size={14} className='wsd-card-two-stat-icon' />
                        </div>
                        <div className='wsd-card-two-stat-value-row'>
                            <span className='wsd-card-two-stat-value'>42</span>
                            <span className='wsd-card-two-stat-change'>+13%</span>
                        </div>
                    </div>
                    <div className='wsd-card-two-stat-card'>
                        <div className='wsd-card-two-stat-card-top'>
                            <span className='wsd-card-two-stat-label'>Provider Calls</span>
                            <Phone size={14} className='wsd-card-two-stat-icon' />
                        </div>
                        <div className='wsd-card-two-stat-value-row'>
                            <span className='wsd-card-two-stat-value'>190</span>
                            <span className='wsd-card-two-stat-change'>+5%</span>
                        </div>
                    </div>
                    <div className='wsd-card-two-stat-card'>
                        <div className='wsd-card-two-stat-card-top'>
                            <span className='wsd-card-two-stat-label'>Pipeline Generated</span>
                            <DollarSign size={14} className='wsd-card-two-stat-icon' />
                        </div>
                        <div className='wsd-card-two-stat-value-row'>
                            <span className='wsd-card-two-stat-value'>$240k</span>
                            <span className='wsd-card-two-stat-change'>+19%</span>
                        </div>
                    </div>
                </div>

                <div className='wsd-card-two-reps'>
                    <span className='wsd-card-two-reps-title'>Care Partnerships Team</span>

                    <div className='wsd-card-two-rep-item'>
                        <img src='https://i.pravatar.cc/60?img=9' alt='Sarah Jenkins' className='wsd-card-two-rep-avatar' />
                        <div className='wsd-card-two-rep-info'>
                            <span className='wsd-card-two-rep-name'>Sarah Jenkins</span>
                            <span className='wsd-card-two-rep-role'>Care Partnerships Lead</span>
                        </div>
                        <div className='wsd-card-two-rep-quota'>
                            <span className='wsd-card-two-rep-quota-value'>124%</span>
                            <span className='wsd-card-two-rep-quota-label'>QUOTA</span>
                        </div>
                        <svg className='wsd-card-two-rep-sparkline' viewBox='0 0 120 40' fill='none' xmlns='http://www.w3.org/2000/svg'>
                            <path d='M0 28 C12 10,22 10,34 22 C46 34,56 34,68 22 C80 10,90 10,102 22 C110 30,115 26,120 24' stroke='#5b8dee' strokeWidth='2' strokeLinecap='round'/>
                        </svg>
                    </div>

                    <div className='wsd-card-two-rep-item'>
                        <img src='https://i.pravatar.cc/60?img=15' alt='Marcus Chen' className='wsd-card-two-rep-avatar' />
                        <div className='wsd-card-two-rep-info'>
                            <span className='wsd-card-two-rep-name'>Marcus Chen</span>
                            <span className='wsd-card-two-rep-role'>Healthcare Account Lead</span>
                        </div>
                        <div className='wsd-card-two-rep-quota'>
                            <span className='wsd-card-two-rep-quota-value'>118%</span>
                            <span className='wsd-card-two-rep-quota-label'>QUOTA</span>
                        </div>
                        <svg className='wsd-card-two-rep-sparkline' viewBox='0 0 120 40' fill='none' xmlns='http://www.w3.org/2000/svg'>
                            <path d='M0 20 C12 34,22 34,34 20 C46 6,56 6,68 20 C80 34,90 34,102 20 C110 10,115 14,120 16' stroke='#5b8dee' strokeWidth='2' strokeLinecap='round'/>
                        </svg>
                    </div>

                    <div className='wsd-card-two-rep-item'>
                        <img src='https://i.pravatar.cc/60?img=20' alt='Emma Rodriguez' className='wsd-card-two-rep-avatar' />
                        <div className='wsd-card-two-rep-info'>
                            <span className='wsd-card-two-rep-name'>Emma Rodriguez</span>
                            <span className='wsd-card-two-rep-role'>Home-care Account Lead</span>
                        </div>
                        <div className='wsd-card-two-rep-quota'>
                            <span className='wsd-card-two-rep-quota-value'>112%</span>
                            <span className='wsd-card-two-rep-quota-label'>QUOTA</span>
                        </div>
                        <svg className='wsd-card-two-rep-sparkline' viewBox='0 0 120 40' fill='none' xmlns='http://www.w3.org/2000/svg'>
                            <path d='M0 22 C8 14,18 14,28 22 C38 30,48 30,58 22 C68 14,78 8,90 16 C100 22,110 26,120 20' stroke='#5b8dee' strokeWidth='2' strokeLinecap='round'/>
                        </svg>
                    </div>

                    <div className='wsd-card-two-rep-item'>
                        <img src='https://i.pravatar.cc/60?img=33' alt='Jordan Park' className='wsd-card-two-rep-avatar' />
                        <div className='wsd-card-two-rep-info'>
                            <span className='wsd-card-two-rep-name'>Jordan Park</span>
                            <span className='wsd-card-two-rep-role'>Care Partnerships Rep</span>
                        </div>
                        <div className='wsd-card-two-rep-quota'>
                            <span className='wsd-card-two-rep-quota-value'>107%</span>
                            <span className='wsd-card-two-rep-quota-label'>QUOTA</span>
                        </div>
                        <svg className='wsd-card-two-rep-sparkline' viewBox='0 0 120 40' fill='none' xmlns='http://www.w3.org/2000/svg'>
                            <path d='M0 30 C10 30,18 16,30 16 C42 16,50 28,62 28 C74 28,84 12,96 12 C106 12,114 20,120 18' stroke='#5b8dee' strokeWidth='2' strokeLinecap='round'/>
                        </svg>
                    </div>

                    <div className='wsd-card-two-rep-item wsd-card-two-rep-item--last'>
                        <img src='https://i.pravatar.cc/60?img=52' alt='Tyler Brooks' className='wsd-card-two-rep-avatar' />
                        <div className='wsd-card-two-rep-info'>
                            <span className='wsd-card-two-rep-name'>Tyler Brooks</span>
                            <span className='wsd-card-two-rep-role'>Hospital Accounts Lead</span>
                        </div>
                        <div className='wsd-card-two-rep-quota'>
                            <span className='wsd-card-two-rep-quota-value'>98%</span>
                            <span className='wsd-card-two-rep-quota-label'>QUOTA</span>
                        </div>
                        <svg className='wsd-card-two-rep-sparkline' viewBox='0 0 120 40' fill='none' xmlns='http://www.w3.org/2000/svg'>
                            <path d='M0 18 C10 10,20 10,30 18 C40 26,50 32,62 26 C74 20,84 24,96 18 C106 12,114 16,120 20' stroke='#5b8dee' strokeWidth='2' strokeLinecap='round'/>
                        </svg>
                    </div>

                    
                </div>

            </div>
        </div>
    )
}

const WorkySalesDemoCardThree = ({ isAnimationEnabled }: AnimatedSalesCardProps): React.JSX.Element => {
    const TARGET_CONV: number = 85;
    const [animatedConv, setAnimatedConv] = useState<number>(0);

    useEffect((): (() => void) => {
        if (!isAnimationEnabled) {
            setAnimatedConv(0);
            return (): void => { return; };
        }

        const t: ReturnType<typeof setTimeout> = setTimeout((): void => {
            setAnimatedConv(TARGET_CONV);
        }, 80);
        return (): void => clearTimeout(t);
    }, [TARGET_CONV, isAnimationEnabled]);

    return(
        <div className='wsd-card'>
            <div className='wsd-card-three-inner'>

                {/* ── Left column ── */}
                <div className='wsd-c3-left'>

                    <div className='wsd-c3-profile'>
                        <div className='wsd-c3-profile-top'>
                            <img src='https://i.pravatar.cc/60?img=9' alt='Sarah Mitchell' className='wsd-c3-avatar' />
                            <div className='wsd-c3-badge-group'>
                                <span className='wsd-c3-decision-badge'>DECISION MAKER 9.8</span>
                                <span className='wsd-c3-match-text'>Match Confidence: High</span>
                            </div>
                        </div>
                        <span className='wsd-c3-name'>Sarah Mitchell</span>
                        <span className='wsd-c3-role'>Director of Care</span>
                        <div className='wsd-c3-contacts'>
                            <div className='wsd-c3-contact-item'>
                                <div className='wsd-c3-contact-icon'><Mail size={10} /></div>
                                <span className='wsd-c3-contact-text'>sarah.m@maplegrovecare.example</span>
                            </div>
                            <div className='wsd-c3-contact-item'>
                                <div className='wsd-c3-contact-icon'><Phone size={10} /></div>
                                <span className='wsd-c3-contact-text'>+1 (416) 555-0144</span>
                            </div>
                            <div className='wsd-c3-contact-item'>
                                <div className='wsd-c3-contact-icon'><Link2 size={10} /></div>
                                <span className='wsd-c3-contact-text'>Profile: Director of Care</span>
                            </div>
                        </div>
                        <button className='wsd-c3-interest-btn'>
                            <Gem size={10} /> Interested in working with an agency <ArrowRight size={11} />
                        </button>

                        <div className='wsd-c3-related-contacts'>
                            <div className='wsd-c3-related-contact'>
                                <img src='https://i.pravatar.cc/60?img=32' alt='Daniel Yoo' className='wsd-c3-related-avatar' />
                                <div className='wsd-c3-related-info'>
                                    <span className='wsd-c3-related-name'>Daniel Yoo</span>
                                    <span className='wsd-c3-related-role'>Care Operations Manager</span>
                                </div>
                                <button className='wsd-c3-related-add' aria-label='Add contact'>
                                    <Plus size={12} />
                                </button>
                            </div>
                            <div className='wsd-c3-related-contact'>
                                <img src='https://i.pravatar.cc/60?img=44' alt='Priya Nair' className='wsd-c3-related-avatar' />
                                <div className='wsd-c3-related-info'>
                                    <span className='wsd-c3-related-name'>Priya Nair</span>
                                    <span className='wsd-c3-related-role'>Nursing Director</span>
                                </div>
                                <button className='wsd-c3-related-add' aria-label='Add contact'>
                                    <Plus size={12} />
                                </button>
                            </div>
                        </div>

                    </div>

                    <div className='wsd-c3-company'>
                        <div className='wsd-c3-company-header'>
                            <div className='wsd-c3-company-logo'>MG</div>
                            <div className='wsd-c3-company-info'>
                                <span className='wsd-c3-company-name'>Maple Grove Care</span>
                                <span className='wsd-c3-company-url'>maplegrovecare.example</span>
                            </div>
                            <div className='wsd-c3-company-actions'>
                                <Globe size={13} />
                                <Snowflake size={13} />
                            </div>
                        </div>
                        <p className='wsd-c3-company-desc'>
                            Illustrative Ontario home-care provider supporting older adults at home. Expanding its care team and exploring agency staffing support.
                        </p>
                        <div className='wsd-c3-company-data'>
                            <div className='wsd-c3-data-item'>
                                <Calendar size={11} className='wsd-c3-data-icon' />
                                <span className='wsd-c3-stat-label'>FOUNDED</span>
                                <span className='wsd-c3-stat-value'>2018</span>
                            </div>
                            <div className='wsd-c3-data-item'>
                                <Users size={11} className='wsd-c3-data-icon' />
                                <span className='wsd-c3-stat-label'>EMPLOYEES</span>
                                <span className='wsd-c3-stat-value'>120</span>
                            </div>
                            <div className='wsd-c3-data-item'>
                                <Briefcase size={11} className='wsd-c3-data-icon' />
                                <span className='wsd-c3-stat-label'>SCALE</span>
                                <span className='wsd-c3-stat-value'>Regional</span>
                            </div>
                            <div className='wsd-c3-data-item'>
                                <DollarSign size={11} className='wsd-c3-data-icon' />
                                <span className='wsd-c3-stat-label'>EST. REVENUE</span>
                                <span className='wsd-c3-stat-value'>$8M / year</span>
                            </div>
                        </div>
                    </div>

                </div>

                {/* ── Right column ── */}
                <div className='wsd-c3-right'>

                    {/* Top row: Conv Prob + Intent Signals */}
                    <div className='wsd-c3-right-top'>

                        <div className='wsd-c3-conv-prob'>
                            <span className='wsd-c3-section-label'>CONVERSION PROBABILITY</span>
                            <div className='wsd-c3-conv-ring-wrapper'>
                                <CircularProgressbar
                                    value={animatedConv}
                                    strokeWidth={7}
                                    styles={{
                                        path: { stroke: '#3b82f6', transition: 'stroke-dashoffset 1.4s ease-in-out 0s' },
                                        trail: { stroke: '#e2e8f0' }
                                    }}
                                />
                                <div className='wsd-c3-conv-ring-inner'>
                                    <span className='wsd-c3-conv-value'>{TARGET_CONV}%</span>
                                </div>
                            </div>


                            <div className='wsd-c3-market-pos'>
                                <span className='wsd-c3-section-label'>CARE TEAM PROFILE</span>
                                <div className='wsd-c3-market-row'>
                                    <span className='wsd-c3-market-label'>Care Team Growth</span>
                                    <span className='wsd-c3-market-badge-green'>+12.4%</span>
                                </div>
                                <div className='wsd-c3-progress-bar'>
                                    <div className='wsd-c3-progress-fill wsd-c3-progress-fill--green' style={{ width: '78%' }}></div>
                                </div>
                                <div className='wsd-c3-market-row' style={{ marginTop: '10px' }}>
                                    <span className='wsd-c3-market-label'>Agency Fit</span>
                                    <span className='wsd-c3-market-value'>Top 15%</span>
                                </div>
                                <div className='wsd-c3-progress-bar'>
                                    <div className='wsd-c3-progress-fill wsd-c3-progress-fill--blue' style={{ width: '55%' }}></div>
                                </div>
                            </div>


                        </div>

                        <div className='wsd-c3-intent'>
                            <div className='wsd-c3-intent-header'>
                                <span className='wsd-c3-intent-title'>Intent Signals</span>
                            </div>
                            <div className='wsd-c3-intent-cards'>
                                <div className='wsd-c3-intent-card'>
                                    <div className='wsd-c3-intent-icon wsd-c3-intent-icon--orange'><Search size={12} /></div>
                                    <span className='wsd-c3-intent-card-title'>Staffing Search</span>
                                </div>
                                <div className='wsd-c3-intent-card'>
                                    <div className='wsd-c3-intent-icon wsd-c3-intent-icon--blue'><Eye size={12} /></div>
                                    <span className='wsd-c3-intent-card-title'>Agency Profile</span>
                                </div>
                                <div className='wsd-c3-intent-card'>
                                    <div className='wsd-c3-intent-icon wsd-c3-intent-icon--green'><FileText size={12} /></div>
                                    <span className='wsd-c3-intent-card-title'>Care Guide</span>
                                </div>
                            </div>
                        </div>

                    </div>

                    {/* Bottom row: Market Position + Curator Insights */}
                    <div className='wsd-c3-right-bottom'>

                        

                        <div className='wsd-c3-curator'>
                            <div className='wsd-c3-curator-header'>
                                <div className='wsd-c3-curator-icon'><Sparkles size={11} fill="white" /> Worky AI Insights</div>
                                <button className='wsd-card-two-header-btn' style={{padding: '7px 10px'}}>Learn More <ChevronDown size={13} strokeWidth={2.5} style={{transform: 'rotate(-90deg)'}}/></button>
                            </div>
                            <div className='wsd-c3-curator-body'>
                                <div className='wsd-c3-curator-section'>
                                    <div className='wsd-c3-cs-icon wsd-c3-cs-icon--active'><Star size={11} strokeWidth={3} /></div>
                                    <div className='wsd-c3-cs-content'>
                                        <span className='wsd-c3-cs-title'>Home-care team is growing</span>
                                        <span className='wsd-c3-cs-desc'>Maple Grove is expanding home visits and seeking PSWs, nurses and care coordinators.</span>
                                    </div>
                                </div>
                                <div className='wsd-c3-curator-section'>
                                    <div className='wsd-c3-cs-icon wsd-c3-cs-icon--active'><Star size={11} strokeWidth={3} /></div>
                                    <div className='wsd-c3-cs-content'>
                                        <span className='wsd-c3-cs-title'>Upcoming staffing needs</span>
                                        <span className='wsd-c3-cs-desc'>Weekday and weekend coverage needs suggest a timely conversation with the care team.</span>
                                    </div>
                                </div>
                                <div className='wsd-c3-curator-section'>
                                    <div className='wsd-c3-cs-icon wsd-c3-cs-icon--active'><Star size={11} strokeWidth={3} /></div>
                                    <div className='wsd-c3-cs-content'>
                                        <span className='wsd-c3-cs-title'>Relevant roles for your agency</span>
                                        <span className='wsd-c3-cs-desc'>Prioritize PSW and nursing support, with location and availability discussed upfront.</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>

                </div>

            </div>
        </div>
    )
}

const WorkySalesDemoCardFour = ({ isAnimationEnabled }: AnimatedSalesCardProps): React.JSX.Element => {
    const [barsVisible, setBarsVisible] = useState<boolean>(false);

    useEffect((): (() => void) => {
        if (!isAnimationEnabled) {
            setBarsVisible(false);
            return (): void => { return; };
        }

        const barsTimeout: ReturnType<typeof setTimeout> = setTimeout((): void => {
            setBarsVisible(true);
        }, 160);

        return (): void => {
            clearTimeout(barsTimeout);
        };
    }, [isAnimationEnabled]);

    return (
        <div className='wsd-card'>
            <div className='wsd-card-four-inner'>

                <div className='wsd-c4-header'>
                    <div className='wsd-c4-header-left'>
                        <span className='wsd-c4-title'>Care Lead Pipeline</span>
                        <span className='wsd-c4-subtitle'>Illustrative care-provider sales pipeline</span>
                    </div>
                    <div className='wsd-c4-live-badge'>SAMPLE PIPELINE</div>
                </div>

                <div className='wsd-c4-body'>

                    <div className='wsd-c4-bars'>
                        <div className='wsd-c4-bar-item'>
                            <div className='wsd-c4-bar-label-row'>
                                <span className='wsd-c4-bar-label wsd-c4-bar-label--blue'>CARE PROVIDERS</span>
                                <span className='wsd-c4-bar-value'>420</span>
                            </div>
                            <div className='wsd-c4-bar-track'>
                                <div className='wsd-c4-bar-fill wsd-c4-bar-fill--blue' style={{ width: barsVisible ? '100%' : '0%' }} />
                            </div>
                        </div>

                        <div className='wsd-c4-bar-item'>
                            <div className='wsd-c4-bar-label-row'>
                                <span className='wsd-c4-bar-label wsd-c4-bar-label--orange'>CONTACTED</span>
                                <span className='wsd-c4-bar-value'>380</span>
                            </div>
                            <div className='wsd-c4-bar-track'>
                                <div className='wsd-c4-bar-fill wsd-c4-bar-fill--orange' style={{ width: barsVisible ? '90%' : '0%' }} />
                            </div>
                        </div>

                        <div className='wsd-c4-bar-item'>
                            <div className='wsd-c4-bar-label-row'>
                                <span className='wsd-c4-bar-label wsd-c4-bar-label--teal'>INTERESTED</span>
                                <span className='wsd-c4-bar-value'>125</span>
                            </div>
                            <div className='wsd-c4-bar-track'>
                                <div className='wsd-c4-bar-fill wsd-c4-bar-fill--teal' style={{ width: barsVisible ? '30%' : '0%' }} />
                            </div>
                        </div>
                    </div>

                    <div className='wsd-c4-stats-panel'>
                        <span className='wsd-c4-stats-label'>TOTAL CONVERSION</span>
                        <div className='wsd-c4-stats-rate'>
                            <span className='wsd-c4-stats-rate-value'>29.8%</span>
                            <TrendingUp size={20} color='#22c55e' strokeWidth={2.5} />
                        </div>
                        <div className='wsd-c4-sparkline'>
                            <svg viewBox='0 0 140 44' xmlns='http://www.w3.org/2000/svg' preserveAspectRatio='none'>
                                <defs>
                                    <linearGradient id='wsd-c4-spark-fill' x1='0' y1='0' x2='0' y2='1'>
                                        <stop offset='0%' stopColor='#22c55e' stopOpacity='0.18' />
                                        <stop offset='100%' stopColor='#22c55e' stopOpacity='0' />
                                    </linearGradient>
                                </defs>
                                <path
                                    d='M0,38 C15,36 25,30 40,28 C55,26 60,20 75,16 C90,12 105,14 120,8 L140,5'
                                    fill='none'
                                    stroke='#22c55e'
                                    strokeWidth='2'
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                />
                                <path
                                    d='M0,38 C15,36 25,30 40,28 C55,26 60,20 75,16 C90,12 105,14 120,8 L140,5 L140,44 L0,44 Z'
                                    fill='url(#wsd-c4-spark-fill)'
                                />
                            </svg>
                        </div>
                        <span className='wsd-c4-stats-growth'>+4.2% from last quarter</span>
                        <button className='wsd-c4-pipeline-btn'>VIEW CARE LEAD PIPELINE</button>
                    </div>

                </div>

            </div>
        </div>
    );
}


const CARD_COUNT: number = 4;
const AUTO_CYCLE_MS: number = 11111;

type CarouselPosition = 'center' | 'left' | 'right' | 'hidden';

const getCarouselPosition = (cardIndex: number, activeIndex: number): CarouselPosition => {
    const diff: number = (cardIndex - activeIndex + CARD_COUNT) % CARD_COUNT;
    if (diff === 0) return 'center';
    if (diff === 1) return 'right';
    if (diff === 3) return 'left';
    return 'hidden';
};

const CAROUSEL_CARDS: readonly React.ComponentType<AnimatedSalesCardProps>[] = [
    WorkySalesDemoCardOne,
    WorkySalesDemoCardTwo,
    WorkySalesDemoCardThree,
    WorkySalesDemoCardFour,
];

const WorkySalesDemo = (): React.JSX.Element => {
    const [activeIndex, setActiveIndex] = useState<number>(0);
    const [isInViewport, setIsInViewport] = useState<boolean>(false);
    const containerRef = useRef<HTMLDivElement | null>(null);

    useEffect((): (() => void) | void => {
        const containerElement: HTMLDivElement | null = containerRef.current;
        if (!containerElement) return undefined;

        const observer: IntersectionObserver = new IntersectionObserver(
            (entries: readonly IntersectionObserverEntry[]): void => {
                const firstEntry: IntersectionObserverEntry | undefined = entries[0];
                setIsInViewport(Boolean(firstEntry?.isIntersecting));
            },
            {
                threshold: 0.1,
            }
        );

        observer.observe(containerElement);

        return (): void => {
            observer.disconnect();
        };
    }, []);

    useEffect((): (() => void) => {
        if (!isInViewport) {
            return (): void => { return; };
        }

        const interval: ReturnType<typeof setInterval> = setInterval((): void => {
            setActiveIndex((prev: number) => (prev + 1) % CARD_COUNT);
        }, AUTO_CYCLE_MS);
        return (): void => clearInterval(interval);
    }, [isInViewport]);

    const isMobile: boolean = useIsMobile();

    return (
        <div className={isMobile?'wsd-carousel-wrapper-mobile':'wsd-carousel-wrapper'} ref={containerRef}>
            <div className={isMobile?'wsd-container-mobile':'wsd-container'}>
                {CAROUSEL_CARDS.map((Card: React.ComponentType<AnimatedSalesCardProps>, index: number) => {
                    const position: CarouselPosition = getCarouselPosition(index, activeIndex);
                    return (
                        <div
                            key={index}
                            className={`wsd-carousel-slot wsd-carousel-slot--${position}`}
                            onClick={() => setActiveIndex(index)}
                        >
                            <Card isAnimationEnabled={isInViewport} />
                        </div>
                    );
                })}
            </div>
            {!isMobile && (
                <div className='wsd-carousel-dots' aria-label='Sales demo card navigation'>
                    {CAROUSEL_CARDS.map((_card: React.ComponentType<AnimatedSalesCardProps>, index: number) => {
                        const isActive: boolean = activeIndex === index;
                        return (
                            <button
                                key={index}
                                type='button'
                                className={`wsd-carousel-dot${isActive ? ' wsd-carousel-dot--active' : ''}`}
                                onClick={() => setActiveIndex(index)}
                                aria-label={`Show sales demo card ${index + 1}`}
                                aria-pressed={isActive}
                            />
                        );
                    })}
                </div>
            )}
        </div>
    );
}

export { WorkySalesDemo };
